import { useGetIdentity, useListContext, useTranslate } from "ra-core";
import { List } from "@/components/admin/list";
import { SortButton } from "@/components/admin/sort-button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { TopToolbar } from "../layout/TopToolbar";
import { ChoiceBadge } from "../misc/ChoiceBadge";
import type { Task } from "../types";
import { AddTask } from "./AddTask";
import { TaskActionsMenu } from "./TaskActions";
import { TaskCheck } from "./TaskCheck";
import { useTaskActions } from "./useTaskActions";
import { TaskDueDate, TaskTarget, TaskTypeLabel } from "./TaskFields";
import { TaskListFilter } from "./TaskListFilter";
import { useTaskStatusChoices } from "./taskStatuses";

/** Desktop « Tâches » page: filters on the left, one row per task, like the contacts list. */
export const TaskList = () => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  if (!identity) return null;

  return (
    <List
      title={translate("resources.tasks.name", { smart_count: 2 })}
      actions={<TaskListActions />}
      perPage={25}
      sort={{ field: "due_date", order: "ASC" }}
      filterDefaultValues={{ status: "todo", sales_id: identity.id }}
      exporter={false}
    >
      <div className="flex flex-row gap-8">
        <TaskListFilter />
        <div className="w-full min-w-0">
          <Card className="py-0 gap-0 overflow-hidden">
            <TaskListContent />
          </Card>
        </div>
      </div>
    </List>
  );
};

const TaskListActions = () => (
  <TopToolbar>
    <SortButton fields={["due_date", "done_date", "created_at"]} />
    <AddTask display="button" selectContact />
  </TopToolbar>
);

const TaskListContent = () => {
  const translate = useTranslate();
  const { data, isPending } = useListContext<Task>();
  if (isPending) return null;
  if (!data?.length) {
    return (
      <p className="p-6 text-sm text-muted-foreground">
        {translate("resources.tasks.empty")}
      </p>
    );
  }
  return (
    <ul className="divide-y">
      {data.map((task) => (
        <TaskRow key={task.id} task={task} />
      ))}
    </ul>
  );
};

const TaskRow = ({ task }: { task: Task }) => {
  const actions = useTaskActions(task);
  const taskStatusChoices = useTaskStatusChoices();

  return (
    <li className="flex items-center gap-4 px-4 py-3 hover:bg-muted/40">
      <TaskCheck isDone={actions.isDone} onToggle={actions.toggleDone} />
      <div className="flex-1 min-w-0">
        <div
          className={cn(
            "font-medium truncate",
            actions.isClosed && "text-muted-foreground",
            actions.isDone && "line-through",
          )}
        >
          {task.text}
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground min-w-0">
          {actions.isAbandoned ? (
            <ChoiceBadge choices={taskStatusChoices} value="abandoned" />
          ) : task.type && task.type !== "none" ? (
            <Badge variant="secondary" className="font-normal shrink-0">
              <TaskTypeLabel task={task} />
            </Badge>
          ) : null}
          {task.note && <span className="truncate">{task.note}</span>}
        </div>
      </div>
      <div className="hidden md:block w-48 xl:w-60 text-sm">
        <TaskTarget task={task} />
      </div>
      <TaskDueDate task={task} className="w-36 shrink-0 text-sm text-right" />
      <TaskActionsMenu task={task} actions={actions} />
    </li>
  );
};
